import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  View,
  Text,
  Platform,
  TouchableOpacity,
  Alert,
  StyleSheet,
  ActivityIndicator,
  FlatList,
} from "react-native";
import ActionSheetIOS from "../../utils/helpers/ActionSheetIOS";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import * as ImagePicker from "expo-image-picker";
import { useSelector } from "react-redux";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  writeBatch,
  serverTimestamp,
  where,
  orderBy,
  query,
  updateDoc,
  arrayUnion,
  Timestamp,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase/config";
import UploadPhotoBox from "../../components/UploadPhotoBox";
import { uploadImage } from "../../utils/helpers/functions";
import Swal from "sweetalert2";
import SectionCard from "../../components/SectionCard";
import SelectField from "../../components/SelectField";
import TextAreaField from "../../components/TextAreaField";
import EmptyState from "../../components/EmptyState";
import {
  normalizeFrequency,
  getFrequencyLabel,
  getPeriodContextLabel,
  isTaskCompletedThisPeriod,
  getDueStatusLabel,
  getDoneStatusLabel,
  getPeriodKey,
  parseFirestoreDate,
  isWithinCurrentPeriod
} from "../../components/scheduleTaskForm/functions"
import {
  executeFirestoreWriteWithQueue,
  isDeviceOnline,
} from "../../utils/offlineWriteQueue";
import { queueDeferredMediaUpload } from "../../utils/deferredMediaQueue";

const lastYear = new Date().getFullYear() - 1;

const toDateTimeLocalValue = (dateValue) => {
  const date = parseFirestoreDate(dateValue) || new Date();
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const resolveMediaUrlsForSubmit = async (mediaItems, scheduleId) => {
  const currentMedia = Array.isArray(mediaItems) ? mediaItems : [];
  const existingUrls = currentMedia.filter((item) => typeof item === "string" && /^https?:\/\//i.test(item));
  const localFiles = currentMedia.filter((item) => typeof item === "string" && !/^https?:\/\//i.test(item));

  if (!localFiles.length) {
    return existingUrls;
  }

  const uploadedUrls = await uploadImage(localFiles, scheduleId);
  return [...existingUrls, ...uploadedUrls];
};

const ScheduledTasksForm = ({ route, navigation }) => {
  const [selectedSchedule, setSelectedSchedule] = useState("");
  const [selectedTaskSelections, setSelectedTaskSelections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCompletedTasks, setLoadingCompletedTasks] = useState(false);
  const [comments, setComments] = useState("");
  const [allSchedules, setAllSchedules] = useState([]);
  const [completedTaskEntries, setCompletedTaskEntries] = useState([]);
  const [completedTasksError, setCompletedTasksError] = useState("");
  const [taskFilter, setTaskFilter] = useState("pending");
  const [media, setMedia] = useState([]);
  const [formError, setFormError] = useState("");
  const [scheduleLoadError, setScheduleLoadError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitAgainTaskSelections, setSubmitAgainTaskSelections] = useState([]);
  const [editModeTask, setEditModeTask] = useState(null);
  const [editCompletionDate, setEditCompletionDate] = useState(null);

  const userUid = useSelector((state) => state.auth.uid);
  const { siteId } = route.params;
  const isMountedRef = useRef(true);
  const db = getFirestore(firebaseApp);

  const windowCleaningScheduleRef = useMemo(
    () => collection(db, "BuildingsX", siteId, "windowCleaningSchedule"),
    [db, siteId]
  );

  const selectedScheduleDetails = useMemo(
    () => allSchedules.find((schedule) => schedule.id === selectedSchedule) || null,
    [allSchedules, selectedSchedule]
  );

  const scheduleFrequency = useMemo(
    () => normalizeFrequency(selectedScheduleDetails?.frequency),
    [selectedScheduleDetails]
  );
  const frequencyLabel = useMemo(() => getFrequencyLabel(scheduleFrequency), [scheduleFrequency]);
  const periodContextLabel = useMemo(
    () => getPeriodContextLabel(scheduleFrequency),
    [scheduleFrequency]
  );

  const scheduleTasks = selectedScheduleDetails?.tasks || [];
  const completedResponsesThisPeriod = useMemo(
    () =>
      completedTaskEntries
        .filter((entry) => isWithinCurrentPeriod(entry.completedAt, scheduleFrequency))
        .sort((a, b) => {
          const dateA = parseFirestoreDate(a.completedAt) || new Date(0);
          const dateB = parseFirestoreDate(b.completedAt) || new Date(0);
          return dateB.getTime() - dateA.getTime();
        }),
    [completedTaskEntries, scheduleFrequency]
  );
  const completionCountByTaskMap = useMemo(() => {
    const map = new Map();
    completedResponsesThisPeriod.forEach((entry) => {
      map.set(entry.task, (map.get(entry.task) || 0) + 1);
    });
    return map;
  }, [completedResponsesThisPeriod]);
  const completedHistoryEntries = useMemo(() => {
    const seenCountMap = new Map();
    return completedResponsesThisPeriod.map((entry) => {
      const nextOccurrence = (seenCountMap.get(entry.task) || 0) + 1;
      seenCountMap.set(entry.task, nextOccurrence);
      return {
        ...entry,
        occurrenceIndex: nextOccurrence,
        totalForTask: completionCountByTaskMap.get(entry.task) || 1,
      };
    });
  }, [completedResponsesThisPeriod, completionCountByTaskMap]);
  const completedThisPeriodTaskNameSet = useMemo(
    () =>
      new Set(
        scheduleTasks.filter((taskName) =>
          isTaskCompletedThisPeriod(taskName, scheduleFrequency, completedTaskEntries)
        )
      ),
    [scheduleTasks, scheduleFrequency, completedTaskEntries]
  );
  const completedCount = useMemo(
    () => scheduleTasks.filter((taskName) => completedThisPeriodTaskNameSet.has(taskName)).length,
    [scheduleTasks, completedThisPeriodTaskNameSet]
  );
  const taskRows = useMemo(() => {
    return scheduleTasks.filter((taskName) => {
      if (taskFilter === "all") {
        return true;
      }

      const isCompleted = completedThisPeriodTaskNameSet.has(taskName);
      if (taskFilter === "completed") {
        return isCompleted;
      }
      return !isCompleted;
    });
  }, [scheduleTasks, taskFilter, completedThisPeriodTaskNameSet]);

  const latestCompletedByTask = useMemo(() => {
    const latestMap = new Map();

    completedTaskEntries.forEach((entry) => {
      if (!entry.task) {
        return;
      }

      const existing = latestMap.get(entry.task);
      const existingDate = parseFirestoreDate(existing?.completedAt);
      const entryDate = parseFirestoreDate(entry.completedAt);

      if (!existing || (entryDate && (!existingDate || entryDate > existingDate))) {
        latestMap.set(entry.task, entry);
      }
    });

    return Array.from(latestMap.values()).sort((a, b) => {
      const dateA = parseFirestoreDate(a.completedAt) || new Date(0);
      const dateB = parseFirestoreDate(b.completedAt) || new Date(0);
      return dateB.getTime() - dateA.getTime();
    });
  }, [completedTaskEntries]);
  const latestResponseByTaskMap = useMemo(() => {
    const map = new Map();
    latestCompletedByTask.forEach((entry) => {
      map.set(entry.task, entry);
    });
    return map;
  }, [latestCompletedByTask]);
  const selectedTaskCount = selectedTaskSelections.length;
  const scheduleLabel = selectedScheduleDetails?.name || "Please select a schedule";

  const formatCompletedDate = useCallback((value) => {
    if (!value) {
      return "Unknown";
    }

    const dateValue = parseFirestoreDate(value);
    if (!dateValue) {
      return "Unknown";
    }

    if (Number.isNaN(dateValue.getTime())) {
      return "Unknown";
    }

    return dateValue.toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const toggleTaskSelection = useCallback((taskName) => {
    if (completedThisPeriodTaskNameSet.has(taskName)) {
      return;
    }

    setSelectedTaskSelections((previous) => {
      if (previous.includes(taskName)) {
        return previous.filter((task) => task !== taskName);
      }
      return [...previous, taskName];
    });
  }, [completedThisPeriodTaskNameSet]);

  const handleToggleSubmitAgain = useCallback((taskName) => {
    setEditModeTask(null);
    setEditCompletionDate(null);

    setSubmitAgainTaskSelections((previous) => {
      if (previous.includes(taskName)) {
        return previous.filter((task) => task !== taskName);
      }
      return [...previous, taskName];
    });

    setSelectedTaskSelections((previous) => {
      if (previous.includes(taskName)) {
        return previous.filter((task) => task !== taskName);
      }
      return [...previous, taskName];
    });
  }, []);

  const handleEditTask = useCallback((taskName) => {
    const latestEntry = latestResponseByTaskMap.get(taskName);
    if (!latestEntry) {
      return;
    }

    setSubmitAgainTaskSelections((previous) => previous.filter((task) => task !== taskName));
    setSelectedTaskSelections([]);
    setEditModeTask(latestEntry);
    setComments(latestEntry.comments || "");
    setMedia(Array.isArray(latestEntry.images) ? latestEntry.images : []);
    setEditCompletionDate(parseFirestoreDate(latestEntry.completedAt) || new Date());
  }, [latestResponseByTaskMap]);

  const pickEditCompletionDate = useCallback(async () => {
    if (!editModeTask) {
      return;
    }

    if (Platform.OS === "web") {
      const result = await Swal.fire({
        title: "Select completion date",
        input: "datetime-local",
        inputValue: toDateTimeLocalValue(editCompletionDate || new Date()),
        showCancelButton: true,
        confirmButtonText: "Use this date",
        cancelButtonText: "Cancel",
        confirmButtonColor: "#2563eb",
      });

      if (!result.isConfirmed || !result.value) {
        return;
      }

      const selectedDate = new Date(result.value);
      if (!Number.isNaN(selectedDate.getTime())) {
        setEditCompletionDate(selectedDate);
      }
      return;
    }

    Alert.alert("Completion date", "Date picker is available in web PWA mode. Using current time.", [
      { text: "OK", onPress: () => setEditCompletionDate(new Date()) },
    ]);
  }, [editModeTask, editCompletionDate]);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    setSelectedTaskSelections([]);
    setSubmitAgainTaskSelections([]);
    setEditModeTask(null);
    setEditCompletionDate(null);
    setComments("");
    setMedia([]);
  }, [selectedSchedule]);

  useEffect(() => {
    setTaskFilter("pending");
  }, [selectedSchedule]);

  const getSchedules = useCallback(async () => {
    if (isMountedRef.current) {
      setLoading(true);
      setScheduleLoadError("");
    }

    try {
      const schedules = await getDocs(windowCleaningScheduleRef);
      const schedulesArray = [];

      if (!isMountedRef.current) {
        return;
      }

      if (schedules.empty) {
        setAllSchedules([]);
        return;
      }

      schedules.forEach((eq) => {
        schedulesArray.push({ id: eq.id, ...eq.data() });
      });

      setAllSchedules(schedulesArray);
    } catch (error) {
      if (isMountedRef.current) {
        setScheduleLoadError("Unable to load schedules. Please check your connection and try again.");
      }
      console.log(error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [windowCleaningScheduleRef]);

  useEffect(() => {
    getSchedules();
  }, [getSchedules]);

  const getCompletedTasks = useCallback(async () => {
    if (!selectedSchedule) {
      setCompletedTaskEntries([]);
      setCompletedTasksError("");
      return;
    }

    if (isMountedRef.current) {
      setLoadingCompletedTasks(true);
      setCompletedTasksError("");
    }

    try {
      const responsesRef = collection(
        windowCleaningScheduleRef,
        selectedSchedule,
        "responses"
      );
      const q = query(responsesRef, orderBy("completedAt", "desc"), where("completedAt", ">=", new Date(lastYear, 0, 1)));

      const responsesSnapshot = await getDocs(q);

      if (!isMountedRef.current) {
        return;
      }

      const responseEntries = responsesSnapshot.docs
        .map((entryDoc) => {
          const data = entryDoc.data() || {};
          return {
            id: entryDoc.id,
            task: data.task || "Unnamed Task",
            completedAt: data.completedAt || data.date || null,
            completedBy: data.completedBy || data.operatives?.[0] || "Unknown",
            imagesCount: Array.isArray(data.images) ? data.images.length : 0,
            comments: data.comments || "",
            images: Array.isArray(data.images) ? data.images : [],
            status: data.status || "completed",
          };
        })
        .sort((a, b) => {
          const dateA = typeof a.completedAt?.toDate === "function" ? a.completedAt.toDate() : new Date(a.completedAt || 0);
          const dateB = typeof b.completedAt?.toDate === "function" ? b.completedAt.toDate() : new Date(b.completedAt || 0);
          return dateB.getTime() - dateA.getTime();
        });

      setCompletedTaskEntries(responseEntries);
    } catch (error) {
      if (isMountedRef.current) {
        setCompletedTasksError("Unable to load completed tasks right now.");
      }
      console.log(error);
    } finally {
      if (isMountedRef.current) {
        setLoadingCompletedTasks(false);
      }
    }
  }, [selectedSchedule, windowCleaningScheduleRef]);

  useEffect(() => {
    getCompletedTasks();
  }, [getCompletedTasks]);

  const openFormMenuOnIos = () => {
    if (!ActionSheetIOS) {
      return Alert.alert(
        "Unsupported",
        "Action sheets are only supported on iOS in this web build."
      );
    }

    ActionSheetIOS.showActionSheetWithOptions(
      {
        options: ["Cancel", ...allSchedules.map((e) => e.name)],
        cancelButtonIndex: 0,
        userInterfaceStyle: "dark",
      },
      (buttonIndex) => {
        if (buttonIndex === 0) {
          return;
        }
        setSelectedSchedule(allSchedules[buttonIndex - 1].id);
      }
    );
  };

  const submitFormHandler = async () => {
    if (!selectedSchedule) {
      setFormError("Please select a schedule before submitting.");
      return;
    }

    if (!editModeTask && selectedTaskCount === 0) {
      setFormError("Please select a schedule and at least one task before submitting.");
      return;
    }

    const scheduleTaskList = selectedScheduleDetails?.tasks || [];
    const validScheduleTaskSet = new Set(scheduleTaskList);
    const submitAgainSet = new Set(submitAgainTaskSelections);
    const validSelectedTasks = selectedTaskSelections.filter(
      (task) =>
        validScheduleTaskSet.has(task) &&
        (!completedThisPeriodTaskNameSet.has(task) || submitAgainSet.has(task))
    );

    if (!editModeTask && (!validSelectedTasks.length || validSelectedTasks.length !== selectedTaskCount)) {
      setFormError("Your task selection changed. Please review tasks and submit again.");
      setSelectedTaskSelections(validSelectedTasks);
      setSubmitAgainTaskSelections((previous) =>
        previous.filter((task) => validSelectedTasks.includes(task))
      );
      return;
    }

    if (!userUid) {
      setFormError("Unable to identify the current user. Please sign in again.");
      return;
    }

    setFormError("");
    setIsSubmitting(true);

    try {
      const schedulesRef = collection(
        windowCleaningScheduleRef,
        selectedSchedule,
        "responses"
      );

      const isOnline = await isDeviceOnline();
      const localMediaCount = media.filter(
        (item) => typeof item === "string" && !/^https?:\/\//i.test(item)
      ).length;
      const localMediaUris = media.filter(
        (item) => typeof item === "string" && !/^https?:\/\//i.test(item)
      );

      const shouldQueueMediaUpload = !isOnline && localMediaCount > 0;

      const mediaUrls = shouldQueueMediaUpload
        ? media.filter((item) => typeof item === "string" && /^https?:\/\//i.test(item))
        : await resolveMediaUrlsForSubmit(media, selectedSchedule);

      if (editModeTask) {
        const responseRef = doc(schedulesRef, editModeTask.id);
        let queuedMediaUploadId = null;

        if (shouldQueueMediaUpload) {
          queuedMediaUploadId = await queueDeferredMediaUpload({
            label: "scheduled-task-edit",
            mediaUris: localMediaUris,
            storageBasePath: `scheduled-tasks/${siteId}/${selectedSchedule}/${editModeTask.id}`,
            documentPaths: [responseRef.path],
          });
        }

        const previousSnapshot = {
          previousCompletedAt: editModeTask.completedAt || null,
          previousComments: editModeTask.comments || "",
          previousImages: Array.isArray(editModeTask.images) ? editModeTask.images : [],
          previousCompletedBy: editModeTask.completedBy || "Unknown",
        };

        const { queued } = await executeFirestoreWriteWithQueue({
          db,
          queueLabel: "scheduled-tasks-edit",
          writeOperation: async () => {
            await updateDoc(responseRef, {
              completedAt: editCompletionDate ? Timestamp.fromDate(editCompletionDate) : serverTimestamp(),
              status: "completed",
              frequency: scheduleFrequency,
              periodKey: getPeriodKey(scheduleFrequency, editCompletionDate || new Date()),
              comments,
              images: shouldQueueMediaUpload ? [] : mediaUrls,
              mediaUploadStatus: shouldQueueMediaUpload ? "queued" : "uploaded",
              pendingMediaCount: shouldQueueMediaUpload ? localMediaCount : 0,
              queuedMediaUploadId: queuedMediaUploadId || null,
              completedBy: userUid,
              operatives: [userUid],
              updatedAt: serverTimestamp(),
              updatedBy: userUid,
              auditLogs: arrayUnion({
                action: "edit",
                editedAt: new Date(),
                editedBy: userUid,
                ...previousSnapshot,
                newCompletedAt: editCompletionDate || null,
                newComments: comments,
                newImages: mediaUrls,
              }),
            });
          },
        });

        await getCompletedTasks();
        setEditModeTask(null);
        setEditCompletionDate(null);
        setComments("");
        setMedia([]);

        const editSuccessMessage = queued
          ? shouldQueueMediaUpload
            ? "Offline: task update saved. Photos queued for upload."
            : "Offline: task update saved locally and queued for sync."
          : "Task updated successfully.";
        if (Platform.OS === "web") {
          alert(editSuccessMessage);
        } else {
          Alert.alert("Updated", editSuccessMessage);
        }
      } else {
        const batch = writeBatch(db);
        const responseRefs = validSelectedTasks.map(() => doc(schedulesRef));
        let queuedMediaUploadId = null;

        if (shouldQueueMediaUpload) {
          queuedMediaUploadId = await queueDeferredMediaUpload({
            label: "scheduled-task-submit",
            mediaUris: localMediaUris,
            storageBasePath: `scheduled-tasks/${siteId}/${selectedSchedule}/${Date.now()}`,
            documentPaths: responseRefs.map((responseRef) => responseRef.path),
          });
        }

        validSelectedTasks.forEach((taskName) => {
          const responseRef = responseRefs.shift();
          batch.set(responseRef, {
            date: serverTimestamp(),
            completedAt: serverTimestamp(),
            status: "completed",
            frequency: scheduleFrequency,
            periodKey: getPeriodKey(scheduleFrequency),
            task: taskName,
            taskId: taskName,
            scheduleId: selectedSchedule,
            siteId,
            completedBy: userUid,
            operatives: [userUid],
            comments,
            images: shouldQueueMediaUpload ? [] : mediaUrls,
            mediaUploadStatus: shouldQueueMediaUpload ? "queued" : "uploaded",
            pendingMediaCount: shouldQueueMediaUpload ? localMediaCount : 0,
            queuedMediaUploadId: queuedMediaUploadId || null,
          });
        });

        const { queued } = await executeFirestoreWriteWithQueue({
          db,
          queueLabel: "scheduled-tasks-submit",
          writeOperation: async () => {
            await batch.commit();
          },
        });

        await getCompletedTasks();

        setSelectedSchedule("");
        setSelectedTaskSelections([]);
        setSubmitAgainTaskSelections([]);
        setComments("");
        setMedia([]);

        const alertMessage = queued
          ? shouldQueueMediaUpload
            ? `Offline: ${validSelectedTasks.length} task${validSelectedTasks.length > 1 ? "s" : ""} saved. Photos queued for upload.`
            : `Offline: ${validSelectedTasks.length} task${validSelectedTasks.length > 1 ? "s" : ""} saved locally and queued for sync.`
          : `Thanks — ${validSelectedTasks.length} task${validSelectedTasks.length > 1 ? "s have" : " has"
            } been submitted.`;
        if (Platform.OS === "web") {
          alert(alertMessage);
          navigation.navigate("Building");
        } else {
          Alert.alert("Thanks", alertMessage, [
            { text: "OK", onPress: () => navigation.navigate("Building") },
          ]);
        }
      }
    } catch (error) {
      const message = error?.message || "Unable to submit the scheduled task. Please try again.";
      setFormError(message);
      if (Platform.OS !== "web") {
        Alert.alert("Oops", message);
      }
      console.log(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isValid = selectedSchedule !== "" && (selectedTaskCount > 0 || !!editModeTask);

  const pickMedia = async () => {
    if (media.length >= 5) {
      return Swal.fire({
        title: "Oops",
        text: "You can only upload a maximum of 5 files.",
        icon: "error",
        confirmButtonText: "OK",
        confirmButtonColor: "#5e99fa",
      });
    }

    const selectedFiles = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.All,
      allowsMultipleSelection: true,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 1,
      multiple: true,
      videoMaxDuration: 20,
    });

    if (selectedFiles?.canceled) {
      return;
    }

    const assets = selectedFiles?.assets || (selectedFiles?.uri ? [{ uri: selectedFiles.uri }] : []);

    const filteredFiles = assets.filter(
      (file) => file?.uri && !file.uri.endsWith(".svg") && !file.uri.endsWith(".mp3")
    );

    if (!filteredFiles.length) {
      return;
    }

    setMedia((previous) => {
      const nextMedia = [...previous, ...filteredFiles.map((file) => file.uri)].slice(0, 5);
      const wasTrimmed = previous.length + filteredFiles.length > 5;

      if (wasTrimmed) {
        Swal.fire({
          title: "Media limit reached",
          text: "Only the first 5 files were kept.",
          icon: "info",
          confirmButtonText: "OK",
          confirmButtonColor: "#5e99fa",
        });
      }

      return nextMedia;
    });
  };

  if (loading) {
    return (
      <View style={styles.loadingWrapper}>
        <ActivityIndicator size="large" color="#2563eb" />
        <View style={styles.skeletonCard} />
        <View style={styles.skeletonCard} />
      </View>
    );
  }

  if (!allSchedules.length) {
    if (scheduleLoadError) {
      return (
        <EmptyState
          title="Unable to load schedules"
          message={scheduleLoadError}
          actionLabel="Retry"
          onAction={getSchedules}
        />
      );
    }

    return (
      <EmptyState
        title="No schedules available"
        message="There are no scheduled tasks configured for this site yet."
        actionLabel="Back to building"
        onAction={() => navigation.navigate("Building")}
      />
    );
  }

  return (
    <KeyboardAwareScrollView
      contentContainerStyle={styles.root}
      enableOnAndroid
      enableAutomaticScroll={Platform.OS === "ios"}
    >
      <View style={styles.pageHeader}>
        <Text style={styles.pageTitle}>Scheduled Tasks</Text>
        <Text style={styles.pageSubtitle}>
          Report a completed task for the selected schedule and attach evidence if needed.
        </Text>
      </View>

      <View style={styles.sectionBlock}>
        <SectionCard
          title="Schedule selection"
          subtitle="Choose the active schedule for this report."
        >
          <View style={styles.fieldGroup}>
            {Platform.OS === "ios" ? (
              <TouchableOpacity style={styles.iosSelector} onPress={openFormMenuOnIos}>
                <Text
                  style={[
                    styles.iosSelectorText,
                    selectedSchedule ? styles.iosSelectorActiveText : null,
                  ]}
                >
                  {scheduleLabel}
                </Text>
              </TouchableOpacity>
            ) : (
              <SelectField
                label="Schedule"
                value={selectedSchedule}
                placeholder="Select schedule"
                options={allSchedules.map((schedule) => ({
                  label: schedule.name,
                  value: schedule.id,
                }))}
                onValueChange={setSelectedSchedule}
                helperText="Select the schedule that applies to this task."
                error={!selectedSchedule && formError ? "Schedule is required." : undefined}
              />
            )}
          </View>
        </SectionCard>
      </View>

      <View style={styles.sectionBlock}>
        <SectionCard
          title="Tasks"
          subtitle="View progress, filter tasks, and submit what is due for this period."
        >
          {!selectedSchedule ? (
            <Text style={styles.completedHintText}>
              Select a schedule above to view pending and completed tasks.
            </Text>
          ) : (
            <View style={styles.taskList}>
              <View style={styles.progressBarRow}>
                <Text style={styles.progressTitleText}>Progress</Text>
                <Text style={styles.progressValueText}>
                  {completedCount} of {scheduleTasks.length} tasks completed {periodContextLabel}
                </Text>
              </View>
              <View style={styles.progressTrack}>
                <View
                  style={[
                    styles.progressFill,
                    {
                      width: `${
                        scheduleTasks.length
                          ? Math.round((completedCount / scheduleTasks.length) * 100)
                          : 0
                      }%`,
                    },
                  ]}
                />
              </View>

              <Text style={styles.sectionLabel}>Choose one or more tasks</Text>
              <View style={styles.filterRow}>
                {[
                  { key: "pending", label: "Pending Tasks" },
                  { key: "completed", label: "Completed This Period" },
                  { key: "all", label: "All" },
                ].map((option) => {
                  const isActive = taskFilter === option.key;
                  return (
                    <TouchableOpacity
                      key={option.key}
                      style={[styles.filterChip, isActive ? styles.filterChipActive : null]}
                      onPress={() => setTaskFilter(option.key)}
                      activeOpacity={0.85}
                    >
                      <Text style={[styles.filterChipText, isActive ? styles.filterChipTextActive : null]}>
                        {option.label}
                      </Text>
                    </TouchableOpacity>
                  );
                })}
              </View>

              <Text style={styles.selectedCountText}>
                {selectedTaskCount} task{selectedTaskCount === 1 ? "" : "s"} selected
              </Text>
              <View style={styles.frequencyInfoRow}>
                <Text style={styles.frequencyInfoLabel}>Frequency</Text>
                <Text style={styles.frequencyInfoValue}>{frequencyLabel}</Text>
              </View>

              {!!editModeTask && (
                <View style={styles.editModeBanner}>
                  <Text style={styles.editModeBannerTitle}>Edit mode: {editModeTask.task}</Text>
                  <Text style={styles.editModeBannerSubtitle}>
                    Completion date: {formatCompletedDate(editCompletionDate || editModeTask.completedAt)}
                  </Text>
                  <View style={styles.editModeActionRow}>
                    <TouchableOpacity
                      style={styles.smallActionButton}
                      onPress={pickEditCompletionDate}
                      activeOpacity={0.85}
                    >
                      <Text style={styles.smallActionButtonText}>Pick Date</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[styles.smallActionButton, styles.smallActionButtonGhost]}
                      onPress={() => {
                        setEditModeTask(null);
                        setEditCompletionDate(null);
                        setComments("");
                        setMedia([]);
                      }}
                      activeOpacity={0.85}
                    >
                      <Text style={[styles.smallActionButtonText, styles.smallActionButtonGhostText]}>Cancel Edit</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              )}

              {loadingCompletedTasks ? (
                <View style={styles.completedLoadingRow}>
                  <ActivityIndicator size="small" color="#2563eb" />
                  <Text style={styles.completedLoadingText}>Loading completed tasks...</Text>
                </View>
              ) : null}

              {!loadingCompletedTasks && !!completedTasksError ? (
                <TouchableOpacity style={styles.retryCompletedButton} onPress={getCompletedTasks}>
                  <Text style={styles.retryCompletedButtonText}>{completedTasksError} Tap to retry.</Text>
                </TouchableOpacity>
              ) : null}

              {!!formError && selectedTaskCount === 0 && (
                <Text style={styles.taskInlineError}>Select at least one task to continue.</Text>
              )}

              <FlatList
                data={taskRows}
                keyExtractor={(item, index) => `${item}-${index}`}
                renderItem={({ item }) => {
                  const isSelected = selectedTaskSelections.includes(item);
                  const isCompleted = completedThisPeriodTaskNameSet.has(item);
                  const latestCompletedEntry = latestResponseByTaskMap.get(item) || null;
                  const completionCountForTask = completionCountByTaskMap.get(item) || 0;
                  const isQueuedSubmitAgain = submitAgainTaskSelections.includes(item);
                  const isTaskInEditMode = editModeTask?.task === item;
                  const taskStatusLabel = isCompleted
                    ? getDoneStatusLabel(scheduleFrequency)
                    : getDueStatusLabel(scheduleFrequency);

                  const rowContent = (
                    <>
                      <View
                        style={[
                          styles.taskCheckbox,
                          isSelected ? styles.taskCheckboxSelected : null,
                          isCompleted ? styles.taskCheckboxCompleted : null,
                        ]}
                      >
                        <Text
                          style={[
                            styles.taskCheckboxMark,
                            isSelected || isCompleted ? styles.taskCheckboxMarkVisible : null,
                          ]}
                        >
                          ✓
                        </Text>
                      </View>
                      <View style={styles.taskTextBlock}>
                        <Text style={[styles.taskRowText, isSelected ? styles.taskRowTextSelected : null]}>
                          {item}
                        </Text>
                        <View style={styles.taskMetaRow}>
                          <Text style={styles.taskFrequencyBadge}>{frequencyLabel}</Text>
                          <Text
                            style={[
                              styles.taskStatusText,
                              isCompleted ? styles.taskStatusDone : styles.taskStatusDue,
                            ]}
                          >
                            {taskStatusLabel}
                          </Text>
                          {completionCountForTask > 1 && (
                            <Text style={styles.taskMultiCompleteBadge}>{completionCountForTask}x completed</Text>
                          )}
                        </View>
                        {!!latestCompletedEntry && (
                          <Text style={styles.taskLastCompletedText}>
                            Last completed: {formatCompletedDate(latestCompletedEntry.completedAt)}
                          </Text>
                        )}
                        {taskFilter === "completed" && !!latestCompletedEntry && (
                          <View style={styles.taskDetailLineWrap}>
                            <Text style={styles.taskDetailText}>
                              By: {latestCompletedEntry.completedBy === userUid ? "You" : latestCompletedEntry.completedBy}
                            </Text>
                            <Text style={styles.taskDetailText}>Photos: {latestCompletedEntry.imagesCount}</Text>
                          </View>
                        )}

                        {isCompleted && !!latestCompletedEntry && (
                          <View style={styles.completedActionRow}>
                            <TouchableOpacity
                              style={[
                                styles.smallActionButton,
                                isTaskInEditMode ? styles.smallActionButtonActive : null,
                              ]}
                              onPress={() => handleEditTask(item)}
                              activeOpacity={0.85}
                              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                            >
                              <Text style={styles.smallActionButtonText}>Edit</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                              style={[
                                styles.smallActionButton,
                                styles.smallActionButtonGhost,
                                isQueuedSubmitAgain ? styles.smallActionButtonActiveGhost : null,
                              ]}
                              onPress={() => handleToggleSubmitAgain(item)}
                              activeOpacity={0.85}
                              hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
                            >
                              <Text
                                style={[
                                  styles.smallActionButtonText,
                                  styles.smallActionButtonGhostText,
                                  isQueuedSubmitAgain ? styles.smallActionButtonActiveGhostText : null,
                                ]}
                              >
                                {isQueuedSubmitAgain ? "Queued" : "Submit Again"}
                              </Text>
                            </TouchableOpacity>
                          </View>
                        )}
                      </View>
                    </>
                  );

                  if (isCompleted) {
                    return (
                      <View
                        style={[
                          styles.taskRow,
                          isSelected ? styles.taskRowSelected : null,
                          styles.taskRowCompleted,
                        ]}
                      >
                        {rowContent}
                      </View>
                    );
                  }

                  return (
                    <TouchableOpacity
                      style={[
                        styles.taskRow,
                        isSelected ? styles.taskRowSelected : null,
                      ]}
                      onPress={() => toggleTaskSelection(item)}
                      activeOpacity={0.85}
                    >
                      {rowContent}
                    </TouchableOpacity>
                  );
                }}
                ListEmptyComponent={
                  <Text style={styles.emptyTasksText}>
                    {taskFilter === "pending"
                      ? "No pending tasks in this schedule."
                      : taskFilter === "completed"
                        ? `No tasks completed ${periodContextLabel}.`
                        : "This schedule does not have tasks yet."}
                  </Text>
                }
              />

              {taskFilter === "completed" && (
                <View style={styles.historySectionWrap}>
                  <Text style={styles.historySectionTitle}>Completion History ({completedHistoryEntries.length})</Text>
                  {!completedHistoryEntries.length ? (
                    <Text style={styles.emptyTasksText}>No completion entries available for this period.</Text>
                  ) : (
                    <FlatList
                      data={completedHistoryEntries}
                      keyExtractor={(item) => item.id}
                      scrollEnabled={false}
                      renderItem={({ item }) => (
                        <View style={styles.completedCard}>
                          <View style={styles.completedCardHeader}>
                            <Text style={styles.completedTaskName}>{item.task}</Text>
                            <Text style={styles.completedBadge}>
                              #{item.occurrenceIndex} / {item.totalForTask}
                            </Text>
                          </View>
                          <Text style={styles.completedMeta}>Completed: {formatCompletedDate(item.completedAt)}</Text>
                          <Text style={styles.completedMeta}>
                            By: {item.completedBy === userUid ? "You" : item.completedBy}
                          </Text>
                          <Text style={styles.completedMeta}>Photos: {item.imagesCount}</Text>
                          {!!item.comments && <Text style={styles.completedMeta}>Comments: {item.comments}</Text>}
                        </View>
                      )}
                    />
                  )}
                </View>
              )}
            </View>
          )}
        </SectionCard>
      </View>

      <View style={styles.sectionBlock}>
        <SectionCard
          title="Comments & evidence"
          subtitle="Add a note and upload up to 5 supporting images or videos."
        >
          <TextAreaField
            label="Task comments"
            value={comments}
            onChangeText={setComments}
            placeholder="Add details about this task..."
            helperText="Optional, but helpful for handover and audit trails."
          />
          <UploadPhotoBox pickMedia={pickMedia} media={media} />
        </SectionCard>
      </View>

      {!!formError && <Text style={styles.inlineError}>{formError}</Text>}

      <TouchableOpacity
        style={[styles.submitButton, { backgroundColor: isValid ? "#2563eb" : "#9ca3af" }]}
        disabled={!isValid || isSubmitting}
        onPress={submitFormHandler}
        activeOpacity={0.85}
      >
        <Text style={styles.submitButtonText}>
          {isSubmitting
            ? editModeTask
              ? "Updating task..."
              : `Submitting ${selectedTaskCount} task${selectedTaskCount === 1 ? "" : "s"}...`
            : editModeTask
              ? "Update existing completion"
              : "Submit selected tasks"}
        </Text>
      </TouchableOpacity>
    </KeyboardAwareScrollView>
  );
};

const styles = StyleSheet.create({
  root: {
    flexGrow: 1,
    backgroundColor: "#f8fbff",
    paddingTop: 24,
    paddingBottom: 32,
    paddingHorizontal: 16,
    alignItems: "center",
  },
  loadingWrapper: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#f8fbff",
    padding: 24,
  },
  skeletonCard: {
    width: "100%",
    maxWidth: 760,
    height: 100,
    borderRadius: 22,
    backgroundColor: "#e2e8f0",
    marginTop: 16,
  },
  pageHeader: {
    width: "100%",
    maxWidth: 760,
    marginBottom: 18,
  },
  sectionBlock: {
    width: "100%",
    maxWidth: 760,
    marginBottom: 10,
  },
  pageTitle: {
    fontSize: 30,
    fontWeight: "800",
    color: "#111827",
    marginBottom: 8,
  },
  pageSubtitle: {
    fontSize: 15,
    color: "#4b5563",
    lineHeight: 22,
  },
  fieldGroup: {
    width: "100%",
  },
  iosSelector: {
    width: "100%",
    minHeight: 50,
    borderRadius: 16,
    backgroundColor: "#f8fafc",
    borderWidth: 1,
    borderColor: "#d1d5db",
    justifyContent: "center",
    paddingHorizontal: 16,
    marginBottom: 18,
  },
  iosSelectorText: {
    fontSize: 15,
    color: "#6b7280",
  },
  iosSelectorActiveText: {
    color: "#111827",
  },
  taskList: {
    marginTop: 8,
  },
  completedHintText: {
    fontSize: 14,
    color: "#6b7280",
  },
  progressBarRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 8,
  },
  progressTitleText: {
    fontSize: 14,
    fontWeight: "700",
    color: "#1f2937",
  },
  progressValueText: {
    fontSize: 13,
    fontWeight: "700",
    color: "#1d4ed8",
  },
  progressTrack: {
    width: "100%",
    height: 10,
    borderRadius: 999,
    backgroundColor: "#dbeafe",
    overflow: "hidden",
    marginBottom: 14,
  },
  progressFill: {
    height: "100%",
    borderRadius: 999,
    backgroundColor: "#2563eb",
  },
  completedLoadingRow: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
  },
  completedLoadingText: {
    marginLeft: 8,
    color: "#4b5563",
    fontSize: 13,
  },
  retryCompletedButton: {
    backgroundColor: "#fee2e2",
    borderColor: "#fca5a5",
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  retryCompletedButtonText: {
    color: "#991b1b",
    fontWeight: "600",
    fontSize: 13,
  },
  completedCard: {
    borderWidth: 1,
    borderColor: "#d1fae5",
    backgroundColor: "#ecfdf5",
    borderRadius: 14,
    padding: 12,
    marginBottom: 10,
  },
  completedCardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
    gap: 8,
  },
  completedTaskName: {
    flex: 1,
    fontSize: 15,
    color: "#065f46",
    fontWeight: "700",
  },
  completedBadge: {
    fontSize: 12,
    color: "#065f46",
    backgroundColor: "#bbf7d0",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 4,
    fontWeight: "700",
  },
  completedBadgeCurrent: {
    backgroundColor: "#86efac",
  },
  completedMeta: {
    fontSize: 13,
    color: "#065f46",
    marginBottom: 3,
  },
  filterRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 10,
  },
  filterChip: {
    minHeight: 36,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#cbd5e1",
    paddingHorizontal: 12,
    justifyContent: "center",
    backgroundColor: "#f8fafc",
  },
  filterChipActive: {
    borderColor: "#2563eb",
    backgroundColor: "#dbeafe",
  },
  filterChipText: {
    color: "#334155",
    fontSize: 13,
    fontWeight: "600",
  },
  filterChipTextActive: {
    color: "#1d4ed8",
  },
  selectedCountText: {
    fontSize: 13,
    color: "#1d4ed8",
    fontWeight: "700",
    marginBottom: 10,
  },
  frequencyInfoRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  frequencyInfoLabel: {
    color: "#334155",
    fontSize: 13,
    fontWeight: "600",
    marginRight: 8,
  },
  frequencyInfoValue: {
    color: "#1d4ed8",
    fontSize: 13,
    fontWeight: "700",
    backgroundColor: "#dbeafe",
    borderRadius: 999,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  editModeBanner: {
    borderWidth: 1,
    borderColor: "#93c5fd",
    backgroundColor: "#eff6ff",
    borderRadius: 12,
    padding: 10,
    marginBottom: 10,
  },
  editModeBannerTitle: {
    color: "#1e3a8a",
    fontSize: 13,
    fontWeight: "800",
    marginBottom: 4,
  },
  editModeBannerSubtitle: {
    color: "#334155",
    fontSize: 12,
    marginBottom: 8,
  },
  editModeActionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  taskInlineError: {
    fontSize: 13,
    color: "#b91c1c",
    marginBottom: 10,
    fontWeight: "600",
  },
  sectionLabel: {
    fontSize: 14,
    fontWeight: "700",
    color: "#1f2937",
    marginBottom: 12,
  },
  taskRow: {
    minHeight: 52,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "#d1d5db",
    backgroundColor: "#ffffff",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 14,
    marginBottom: 10,
  },
  taskRowSelected: {
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
  },
  taskRowCompleted: {
    borderColor: "#86efac",
    backgroundColor: "#f0fdf4",
  },
  taskCheckbox: {
    width: 24,
    height: 24,
    borderRadius: 7,
    borderWidth: 1,
    borderColor: "#9ca3af",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 12,
  },
  taskCheckboxSelected: {
    backgroundColor: "#2563eb",
    borderColor: "#2563eb",
  },
  taskCheckboxCompleted: {
    backgroundColor: "#16a34a",
    borderColor: "#16a34a",
  },
  taskCheckboxMark: {
    fontSize: 14,
    color: "transparent",
    fontWeight: "900",
    lineHeight: 16,
  },
  taskCheckboxMarkVisible: {
    color: "#ffffff",
  },
  taskRowText: {
    color: "#111827",
    fontSize: 15,
    lineHeight: 20,
    fontWeight: "500",
  },
  taskTextBlock: {
    flex: 1,
  },
  taskRowTextSelected: {
    color: "#1e3a8a",
    fontWeight: "700",
  },
  taskMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
    gap: 8,
  },
  taskFrequencyBadge: {
    fontSize: 11,
    color: "#1e3a8a",
    fontWeight: "700",
    backgroundColor: "#dbeafe",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  taskStatusText: {
    fontSize: 12,
    fontWeight: "700",
  },
  taskStatusDone: {
    color: "#15803d",
  },
  taskStatusDue: {
    color: "#b45309",
  },
  taskMultiCompleteBadge: {
    fontSize: 11,
    color: "#065f46",
    fontWeight: "700",
    backgroundColor: "#d1fae5",
    borderRadius: 999,
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  taskLastCompletedText: {
    marginTop: 4,
    color: "#475569",
    fontSize: 12,
    fontWeight: "500",
  },
  taskDetailLineWrap: {
    marginTop: 4,
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
  },
  taskDetailText: {
    fontSize: 12,
    color: "#065f46",
    fontWeight: "600",
  },
  completedActionRow: {
    marginTop: 6,
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  },
  historySectionWrap: {
    marginTop: 10,
    borderTopWidth: 1,
    borderTopColor: "#e2e8f0",
    paddingTop: 12,
  },
  historySectionTitle: {
    fontSize: 14,
    fontWeight: "800",
    color: "#1f2937",
    marginBottom: 8,
  },
  smallActionButton: {
    minHeight: 36,
    borderRadius: 999,
    paddingHorizontal: 12,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#2563eb",
  },
  smallActionButtonText: {
    color: "#ffffff",
    fontSize: 12,
    fontWeight: "700",
  },
  smallActionButtonGhost: {
    backgroundColor: "#ffffff",
    borderWidth: 1,
    borderColor: "#2563eb",
  },
  smallActionButtonGhostText: {
    color: "#1d4ed8",
  },
  smallActionButtonActive: {
    backgroundColor: "#1d4ed8",
  },
  smallActionButtonActiveGhost: {
    borderColor: "#15803d",
    backgroundColor: "#dcfce7",
  },
  smallActionButtonActiveGhostText: {
    color: "#15803d",
  },
  emptyTasksText: {
    fontSize: 14,
    color: "#6b7280",
    paddingVertical: 8,
  },
  inlineError: {
    width: "100%",
    maxWidth: 760,
    marginBottom: 14,
    padding: 14,
    borderRadius: 18,
    backgroundColor: "#fff1f2",
    borderWidth: 1,
    borderColor: "#fecaca",
    color: "#b91c1c",
    fontSize: 14,
    lineHeight: 20,
    textAlign: "left",
  },
  submitButton: {
    width: "100%",
    maxWidth: 760,
    height: 58,
    borderRadius: 18,
    justifyContent: "center",
    alignItems: "center",
    marginTop: 16,
    shadowColor: "#0f172a",
    shadowOpacity: 0.14,
    shadowOffset: { width: 0, height: 14 },
    shadowRadius: 24,
    elevation: 4,
  },
  submitButtonText: {
    color: "#fff",
    fontWeight: "700",
    fontSize: 16,
  },
});

export default ScheduledTasksForm;
